import {
  Activity,
  BarChart3,
  Briefcase,
  Bug,
  CreditCard,
  Download,
  FileClock,
  FileText,
  HardDrive,
  Layers,
  LayoutList,
  LayoutPanelTop,
  Link2,
  Mail,
  MessageSquare,
  PackageMinus,
  Palette,
  PieChart,
  Radio,
  Settings,
  Shield,
  ShieldAlert,
  Smartphone,
  Users,
} from "lucide-react";

import * as React from "react";

import { useTranslation } from "react-i18next";

import { Link, useLocation } from "react-router-dom";

import { NavUser } from "@/components/nav-user";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";

import { cn } from "@/lib/utils";

import { useIsAdmin, useIsViewerOnly } from "../features/auth/hooks/useHasRole";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation("common");

  const isAdmin = useIsAdmin();

  const isViewerOnly = useIsViewerOnly();

  const location = useLocation();

  const allGroups = [
    {
      label: "Dashboard",

      items: [
        { title: t("NAV.stats"), url: "/stats", icon: PieChart },

        { title: t("NAV.analytics"), url: "/analytics", icon: BarChart3 },
      ],
    },

    {
      label: "Platform",

      items: [
        { title: t("NAV.workspaces"), url: "/workspaces", icon: Briefcase },

        { title: t("NAV.integrations"), url: "/integrations", icon: Link2 },

        { title: t("NAV.cloudStreams"), url: "/cloud-streams", icon: Radio },

        { title: t("NAV.plans"), url: "/plans", icon: LayoutList },

        {
          title: t("NAV.subscriptions"),

          url: "/subscriptions",

          icon: CreditCard,
        },

        { title: t("NAV.roms"), url: "/roms", icon: Smartphone },

        {
          title: t("NAV.overlays", "Overlays"),

          url: "/overlays",

          icon: Layers,
        },

        {
          title: t("NAV.panels", "Panels"),

          url: "/panels",

          icon: LayoutPanelTop,
        },

        {
          title: t("NAV.designs", "Designs"),

          url: "/designs",

          icon: Palette,
        },
      ],
    },

    {
      label: "Users",

      items: [
        { title: t("NAV.usersActive"), url: "/users/active", icon: Users },

        {
          title: t("NAV.usersActivity"),

          url: "/users/activity",

          icon: Activity,
        },

        {
          title: t("NAV.usersUninstalls"),

          url: "/users/uninstalls",

          icon: PackageMinus,
        },
      ],
    },

    {
      label: "System",

      items: [
        { title: t("NAV.settings"), url: "/settings", icon: Settings },

        { title: t("NAV.contact"), url: "/contact", icon: MessageSquare },

        ...(isAdmin
          ? [
              {
                title: t("NAV.support"),

                url: "/support/reports",

                icon: Bug,
              },
            ]
          : []),

        {
          title: t("NAV.emailTemplates"),

          url: "/email-templates",

          icon: FileText,
        },

        { title: t("NAV.newsletter"), url: "/newsletter", icon: Mail },

        { title: t("NAV.installer"), url: "/installer", icon: Download },

        {
          title: t("NAV.changelog", "Changelog"),

          url: "/changelog",

          icon: FileClock,
        },

        { title: t("NAV.guest"), url: "/guest", icon: Layers },

        { title: t("NAV.storage"), url: "/storage", icon: HardDrive },

        {
          title: t("NAV.security", "Seguridad"),

          url: "/security",

          icon: Shield,
        },

        ...(isAdmin
          ? [{ title: t("NAV.admins"), url: "/admins", icon: ShieldAlert }]
          : []),
      ],
    },
  ];

  // Founder (when not also admin/developer) sees Dashboard + read-only

  // Subscriptions + Active Users. Action buttons in those pages are gated

  // separately via useIsViewerOnly() inside each page component.

  const VIEWER_ALLOWED_URLS = new Set([
    "/stats",

    "/analytics",

    "/subscriptions",

    "/users/active",
  ]);

  const groups = isViewerOnly
    ? allGroups

        .map((g) => ({
          ...g,

          items: g.items.filter((i) => VIEWER_ALLOWED_URLS.has(i.url)),
        }))

        .filter((g) => g.items.length > 0)
    : allGroups;

  return (
    <Sidebar
      collapsible="icon"
      {...props}
      className="border-r border-border/50"
    >
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/">
                <img src="/logo.png" alt="Eriol" className="h-8" />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            <SidebarMenu>
              {group.items.map((item) => {
                const isActive = location.pathname.startsWith(item.url);

                return (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton
                      asChild
                      tooltip={item.title}
                      isActive={isActive}
                      className={cn(
                        "hover:bg-accent/20 hover:text-accent-foreground ",

                        isActive &&
                          "bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary font-medium shadow-sm shadow-primary/5",
                      )}
                    >
                      <Link to={item.url}>
                        {item.icon && (
                          <item.icon
                            className={cn(isActive && "text-primary")}
                          />
                        )}
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
